import React from 'react';
import { motion } from 'framer-motion';
import { Users, Heart, Anchor } from 'lucide-react';

const AboutDemo = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  return (
    <section id="nosotros" className="py-20 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-ocean-50 via-pearl-50 to-jade-50" />
      <div className="absolute inset-0 bg-white/80" />
      
      {/* Simplified floating elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-jade-200/20 to-ocean-200/20 rounded-full blur-xl" />
      <div className="absolute bottom-32 right-16 w-40 h-40 bg-gradient-to-r from-seagreen-200/20 to-jade-200/20 rounded-full blur-xl" />
      
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="flex items-center justify-center gap-3 mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Anchor className="text-ocean-500 w-8 h-8" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-transparent bg-gradient-to-r from-ocean-600 to-jade-600 bg-clip-text font-crimson antialiased">
              Sobre Nosotros
            </h2>
          </motion.div>
          <motion.p 
            className="text-lg text-deep-600 max-w-2xl mx-auto font-dmsans antialiased"
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            Llevamos la frescura del mar directamente a tu hogar
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Content */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            
            {/* Main Description */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-white/30 shadow-lg">
              <h3 className="text-2xl font-semibold text-deep-700 mb-4 font-crimson antialiased">
                Mar de Jade
              </h3>
              <p className="text-deep-600 leading-relaxed mb-6 font-dmsans antialiased">
                Somos una empresa familiar dedicada a ofrecer los mejores mariscos frescos 
                del Mar Argentino. Nuestra pasión es acercar la calidad premium del océano 
                a tu mesa, con productos seleccionados y procesados con el máximo cuidado.
              </p>
              <p className="text-deep-600 leading-relaxed font-dmsans antialiased">
                Trabajamos directamente con productores locales para garantizar frescura, 
                calidad y trazabilidad en cada producto que llega a tu hogar.
              </p>
            </div>

            {/* Values */}
            <div className="grid sm:grid-cols-2 gap-6">
              <motion.div
                className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-md text-center hover:shadow-xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <Users className="w-10 h-10 text-ocean-500 mx-auto mb-3" />
                <h4 className="text-lg font-semibold text-deep-700 mb-2 font-crimson antialiased">
                  Familia
                </h4>
                <p className="text-deep-600 text-sm font-dmsans antialiased">
                  Empresa familiar con tradición y compromiso
                </p>
              </motion.div>

              <motion.div
                className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-md text-center hover:shadow-xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <Heart className="w-10 h-10 text-jade-500 mx-auto mb-3" />
                <h4 className="text-lg font-semibold text-deep-700 mb-2 font-crimson antialiased">
                  Pasión
                </h4>
                <p className="text-deep-600 text-sm font-dmsans antialiased">
                  Amor por el mar y sus productos
                </p>
              </motion.div>
            </div>

          </motion.div>

          {/* Image */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/images/oyster-farmer-7874534_1280.jpg"
                alt="Mar de Jade"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ocean-500/20 to-transparent" />
            </div>
            
            {/* Floating badge */}
            <motion.div
              className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-4 shadow-xl border border-white/20"
              animate={{ y: [-5, 5, -5] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-ocean-600 font-crimson">Premium</div>
                <div className="text-sm text-deep-600 font-dmsans">Calidad</div>
              </div>
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default AboutDemo;
